import React, { useEffect, useRef, useState } from 'react'
import { tradingServices, TradingAsset } from '../../services/trading-service'

type Props = {
  /** The chosen asset. Set from outside (e.g. the watchlist) to pre-fill the box. */
  selected: { symbol: string; name: string } | null
  onSelect: (asset: { symbol: string; name: string } | null) => void
  /** Keeps element ids unique when a page has more than one search. */
  id?: string
  label?: string
}

const DEBOUNCE_MS = 250
const RECENT_KEY = 'gbc.trading.recentSearches'
const RECENT_MAX = 5

/** Recently chosen stocks, newest first; kept in this browser only. */
export const readRecent = (): TradingAsset[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : []
  } catch {
    return []
  }
}

export const rememberRecent = (asset: TradingAsset) => {
  try {
    const next = [asset, ...readRecent().filter((a) => a.symbol !== asset.symbol)].slice(
      0,
      RECENT_MAX,
    )
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable (private mode); recent searches are a convenience.
  }
}

/**
 * Autocomplete over tradable US stocks/ETFs. Only a result chosen from the
 * list counts as a valid symbol; typing after choosing clears the selection.
 */
const SymbolSearch = ({ selected, onSelect, id = 'trading-symbol', label = 'Symbol' }: Props) => {
  const [text, setText] = useState('')
  const [results, setResults] = useState<TradingAsset[]>([])
  const [recent, setRecent] = useState<TradingAsset[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [active, setActive] = useState(-1)
  const requestId = useRef(0)
  const wrapper = useRef<HTMLDivElement>(null)

  // Reflect selections made elsewhere (watchlist) in the input.
  useEffect(() => {
    if (selected) {
      setText(`${selected.symbol} — ${selected.name}`)
      setOpen(false)
    }
  }, [selected])

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const search = (query: string) => {
    const id = ++requestId.current
    if (!query.trim()) {
      setResults([])
      setSearched(false)
      setLoading(false)
      return
    }
    setLoading(true)
    window.setTimeout(async () => {
      if (id !== requestId.current) return
      const res = await tradingServices.searchAssets(query.trim())
      if (id !== requestId.current) return
      setResults(res?.status === 200 ? res.data : [])
      setSearched(true)
      setLoading(false)
      setActive(-1)
    }, DEBOUNCE_MS)
  }

  const change = (value: string) => {
    setText(value)
    setOpen(true)
    if (selected) onSelect(null)
    search(value)
  }

  const choose = (asset: TradingAsset) => {
    setOpen(false)
    setResults([])
    rememberRecent(asset)
    onSelect({ symbol: asset.symbol, name: asset.name })
  }

  const showRecent = open && !selected && text.trim() === '' && recent.length > 0
  const options = showRecent ? recent : results

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((i) => Math.min(i + 1, options.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter' && open && options.length > 0) {
      event.preventDefault()
      choose(options[active >= 0 ? active : 0])
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const showList = showRecent || (open && !selected && text.trim() !== '' && (loading || searched))

  return (
    <div className='trading-page__combo' ref={wrapper}>
      <label className='trading-page__field'>
        <span>{label}</span>
        <input
          role='combobox'
          aria-expanded={showList}
          aria-controls={`${id}-list`}
          aria-autocomplete='list'
          aria-activedescendant={active >= 0 ? `${id}-${active}` : undefined}
          value={text}
          onChange={(e) => change(e.target.value)}
          onFocus={() => {
            setRecent(readRecent())
            setActive(-1)
            setOpen(true)
          }}
          onKeyDown={onKeyDown}
          placeholder='Search by symbol or company, e.g. AAPL or Apple'
          autoComplete='off'
        />
      </label>
      {showList ? (
        <ul id={`${id}-list`} role='listbox' className='trading-page__combo-list'>
          {showRecent ? <li className='trading-page__combo-note'>Recent searches</li> : null}
          {!showRecent && loading && results.length === 0 ? (
            <li className='trading-page__combo-note'>Searching…</li>
          ) : null}
          {!showRecent && !loading && searched && results.length === 0 ? (
            <li className='trading-page__combo-note'>No tradable match found</li>
          ) : null}
          {options.map((asset, index) => (
            <li
              key={asset.symbol}
              id={`${id}-${index}`}
              role='option'
              aria-selected={index === active}
              className={`trading-page__combo-option${index === active ? ' is-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault()
                choose(asset)
              }}
              onMouseEnter={() => setActive(index)}
            >
              <strong>{asset.symbol}</strong>
              <span>{asset.name}</span>
              <em>
                {asset.exchange}
                {asset.fractionable ? <b className='trading-page__badge'>Fractional</b> : null}
              </em>
            </li>
          ))}
        </ul>
      ) : null}
      {!selected && text.trim() !== '' && !showList ? (
        <p className='trading-page__hint'>Pick a stock from the list to continue.</p>
      ) : null}
    </div>
  )
}

export { SymbolSearch }
